import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../hooks";
import { addCustomer } from "../../../slices/customerSlice";
import { XCircle } from "lucide-react";

interface AddCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  stores: { id: string; name: string }[];
}

const AddCustomerModal: React.FC<AddCustomerModalProps> = ({ onClose, stores, isOpen }) => {
  const dispatch = useAppDispatch();
  const { loading } = useSelector((state: any) => state.customers);

  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      othername: "",
      email: "",
      phone_number: "",
      store_id: "",
    },
    validationSchema: Yup.object({
      firstname: Yup.string().required("First name is required"),
      lastname: Yup.string().required("Last name is required"),
      email: Yup.string().email("Invalid email format").nullable(),
      phone_number: Yup.string().nullable(),
      store_id: Yup.string().required("Please select a store"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const result = await dispatch(addCustomer(values));
      if (addCustomer.fulfilled.match(result)) {
        resetForm();
        onClose();
      }
    },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-[999]">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="flex justify-between">
          <h2 className="text-xl font-semibold text-primary mb-4">Add Customer</h2>
          <XCircle
            color="red"
            className="cursor-pointer"
            onClick={onClose}
          />
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-3">
          {/* First Name */}
          <div>
            <label htmlFor="firstname" className="form-label">
              Firstname
            </label>
            <input
              name="firstname"
              placeholder="First Name"
              value={formik.values.firstname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded-md px-3 py-2"
            />
            {formik.touched.firstname && formik.errors.firstname && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.firstname}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastname" className="form-label">
              Lastname
            </label>
            <input
              name="lastname"
              placeholder="Last Name"
              value={formik.values.lastname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded-md px-3 py-2"
            />
            {formik.touched.lastname && formik.errors.lastname && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.lastname}</p>
            )}
          </div>

          {/* Other Name */}
          <div>
            <label htmlFor="othername" className="form-label">
              Othername
            </label>
            <input
              name="othername"
              placeholder="Other Name (optional)"
              value={formik.values.othername}
              onChange={formik.handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              name="email"
              placeholder="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded-md px-3 py-2"
            />
            {formik.touched.email && formik.errors.email && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.email}</p>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label htmlFor="phone_number" className="form-label">
              Phone Number
            </label>
            <input
              name="phone_number"
              placeholder="Phone Number"
              value={formik.values.phone_number}
              onChange={formik.handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          {/* Store Selection */}
          <div>
            <label htmlFor="store_id" className="form-label">
              Assign to a store
            </label>
            <select
              name="store_id"
              value={formik.values.store_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Select Store</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
            {formik.touched.store_id && formik.errors.store_id && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.store_id}</p>
            )}
          </div>

          {/* Buttons */}
          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-primary text-white px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <span className="loader"></span>
                  <span>Saving...</span>
                </div>
              ) : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCustomerModal;
